import { type FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, Tag, Typography, Space } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, EyeOutlined } from '@ant-design/icons';
import type { BlogPost } from '@/types';
import { trackBlogRead } from '@/utils/analytics';

const { Title, Text } = Typography;

interface BlogCardProps {
  post: BlogPost;
  compact?: boolean;
}

function getYoutubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  return match ? match[1] : null;
}

/**
 * Fixed-height title: exactly 2 lines.
 * Ant Design Title level={4} ≈ 20px, lineHeight 1.4 → ~28px per line.
 * 2 lines = 56px. Overflow gets ellipsis via -webkit-line-clamp.
 */
const TITLE_LINE_H = 28;
const CardTitle: FC<{ text: string }> = ({ text }) => (
  <div
    className="blog-card-title"
    style={{
      height: TITLE_LINE_H * 2,
      marginBottom: 8,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
    }}
  >
    <Title level={4} style={{ marginBottom: 0, letterSpacing: '-0.01em' }}>
      {text}
    </Title>
  </div>
);

/**
 * Fixed-height excerpt with fade-out mask for overflow.
 * Desktop: 3 lines, Mobile: 2 lines.
 * lineHeight = 1.7, fontSize = 14px → lineHeight ≈ 23.8px
 * 3 lines ≈ 71.4px, 2 lines ≈ 47.6px
 */
const LINE_H = 23.8;
const CardExcerpt: FC<{ text: string }> = ({ text }) => (
  <div
    className="blog-card-excerpt"
    style={{
      position: 'relative',
      height: LINE_H * 3,
      overflow: 'hidden',
      marginBottom: 16,
    }}
  >
    <span
      style={{
        fontSize: 14,
        lineHeight: 1.7,
        color: 'var(--color-text-secondary)',
        display: 'block',
      }}
    >
      {text}
    </span>
    {/* Fade-out gradient mask */}
    <div
      className="blog-card-excerpt__fade"
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: LINE_H,
        background: 'linear-gradient(to bottom, transparent, var(--color-bg-elevated))',
        pointerEvents: 'none',
      }}
    />
  </div>
);

const ThumbnailOverlay: FC<{ thumbnail: string | null; compact?: boolean }> = ({ thumbnail, compact }) => (
  <div
    style={{
      position: 'relative',
      background: thumbnail
        ? `url(${thumbnail}) center/cover no-repeat`
        : 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)',
      ...(compact
        ? { width: '33.3%', minWidth: 100, flexShrink: 0, minHeight: 0 }
        : { width: '100%', flex: 1, minHeight: 200 }),
    }}
  >
    {/* Play button */}
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.2)',
      }}
    >
      <svg
        width={compact ? 48 : 68}
        height={compact ? 34 : 48}
        viewBox="0 0 68 48"
        style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))' }}
      >
        <path
          d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55C3.97 2.33 2.27 4.81 1.48 7.74.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z"
          fill="#FF0000"
        />
        <path d="M45 24L27 14v20" fill="#fff" />
      </svg>
    </div>
  </div>
);

const CardMeta: FC<{ date: string; timeLabel: string; timeIcon: 'clock' | 'eye'; tags: string[] }> = ({
  date, timeLabel, timeIcon, tags,
}) => (
  <>
    <Space
      split={<span style={{ color: 'var(--color-separator)' }}>·</span>}
      style={{ fontSize: 13 }}
    >
      <Text type="secondary">
        <CalendarOutlined style={{ marginRight: 4 }} />
        {date}
      </Text>
      <Text type="secondary">
        {timeIcon === 'eye'
          ? <EyeOutlined style={{ marginRight: 4 }} />
          : <ClockCircleOutlined style={{ marginRight: 4 }} />}
        {timeLabel}
      </Text>
    </Space>
    <div style={{ marginTop: 16 }}>
      <Space size={[6, 6]} wrap>
        {tags.map((tag) => (
          <Tag key={tag} style={{ fontSize: 11 }}>
            {tag}
          </Tag>
        ))}
      </Space>
    </div>
  </>
);

const YouTubeCard: FC<BlogCardProps> = ({ post, compact }) => {
  const location = useLocation();
  const videoId = post.youtubeUrl ? getYoutubeId(post.youtubeUrl) : null;
  const thumbnail = videoId
    ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    : null;

  return (
    <Link
      to={`/blog/${post.slug}`}
      state={{ from: location.pathname + location.search }}
      style={{ textDecoration: 'none', display: 'block', height: '100%' }}
      onClick={() => {
        // Save scroll so /blog can restore the user's position on back-nav.
        sessionStorage.setItem('blog-restore-scroll', String(window.scrollY));
        trackBlogRead(post.title);
      }}
    >
      <Card
        className="hoverable-card"
        style={{
          overflow: 'hidden',
          height: '100%',
        }}
        styles={{ body: { padding: 0, height: '100%' } }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: compact ? 'row' : 'column',
            height: '100%',
          }}
        >
          <ThumbnailOverlay thumbnail={thumbnail} compact={compact} />
          <div style={{ padding: 28 }}>
            <Tag style={{ marginBottom: 12 }}>{post.category}</Tag>
            <CardTitle text={post.title} />
            <CardExcerpt text={post.excerpt} />
            <CardMeta date={post.date} timeLabel={post.readTime} timeIcon="eye" tags={post.tags} />
          </div>
        </div>
      </Card>
    </Link>
  );
};

const StandardCard: FC<BlogCardProps> = ({ post }) => {
  const location = useLocation();
  return (
  <Link
    to={`/blog/${post.slug}`}
    state={{ from: location.pathname + location.search }}
    style={{ textDecoration: 'none' }}
    onClick={() => {
      // Save scroll so /blog can restore the user's position on back-nav.
      sessionStorage.setItem('blog-restore-scroll', String(window.scrollY));
      trackBlogRead(post.title);
    }}
  >
    <Card
      className="hoverable-card"
      style={{ height: '100%' }}
      styles={{ body: { padding: 28 } }}
    >
      <Tag style={{ marginBottom: 12 }}>{post.category}</Tag>
      <CardTitle text={post.title} />
      <CardExcerpt text={post.excerpt} />
      <CardMeta date={post.date} timeLabel={post.readTime} timeIcon="clock" tags={post.tags} />
    </Card>
  </Link>
  );
};

const CompactYouTubeCard: FC<BlogCardProps> = ({ post }) => {
  const location = useLocation();
  return (
  <Link
    to={`/blog/${post.slug}`}
    state={{ from: location.pathname + location.search }}
    style={{ textDecoration: 'none' }}
    onClick={() => {
      // Save scroll so /blog can restore the user's position on back-nav.
      sessionStorage.setItem('blog-restore-scroll', String(window.scrollY));
      trackBlogRead(post.title);
    }}
  >
    <Card
      className="hoverable-card"
      style={{ height: '100%' }}
      styles={{ body: { padding: 28 } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <svg width="24" height="17" viewBox="0 0 24 17" fill="#FF0000">
          <path d="M23.5 2.5C23.2.9 22 .2 20.5.1 17.2 0 12 0 12 0S6.8 0 3.5.1C2 .2.8.9.5 2.5.2 4 0 6 0 8.5s.2 4.5.5 6C.8 16.1 2 16.8 3.5 16.9 6.8 17 12 17 12 17s5.2 0 8.5-.1c1.5-.1 2.7-.8 3-2.4.3-1.5.5-3.5.5-6s-.2-4.5-.5-6zM9.6 12.1V4.9l6.4 3.6-6.4 3.6z" />
        </svg>
      </div>
      <CardTitle text={post.title} />
      <CardExcerpt text={post.excerpt} />
      <CardMeta date={post.date} timeLabel={post.readTime} timeIcon="eye" tags={post.tags} />
    </Card>
  </Link>
  );
};

export const BlogCard: FC<BlogCardProps> = ({ post, compact }) => {
  if (post.youtubeUrl) {
    return compact
      ? <CompactYouTubeCard post={post} />
      : <YouTubeCard post={post} />;
  }
  return <StandardCard post={post} />;
};
